import {defineStore} from 'pinia'
import {
  ISetLlmMessageFeedbackRequestInterfaces,
} from '@toolify/server/src/controllers/MessageController/validators/SetLlmMessageFeedback/types/ISetLlmMessageFeedbackRequestInterfaces'
import {LlmMessageFeedbackType} from '@toolify/server/src/services/MessageService/strategies/MessageTypeStrategies/TextMessageTypeStrategy/enum/LlmMessageFeedbackType'
import {ILlmRequestPayload} from '@toolify/server/src/services/LlmService/types/ILlmRequestPayload'
import {useApiRequestStore} from '../../ApiRequestStore/useApiRequestStore'

import {JSONContent} from '@tiptap/core'
import {ConversationType} from '@toolify/server/src/models/mongoose/MessageModel/enum/ConversationType'
import {
  IConversationTypeInterfacesMap,
} from '@toolify/server/src/services/MessageService/types/IConversationTypeInterfacesMap'
import {
  ICreateMessageRequestInterfaces,
} from '@toolify/server/src/controllers/MessageController/validators/CreateMessage/types/ICreateMessageRequestInterfaces'
import {mapMessageResponseInstance} from '@toolify/client/src/modules/api/singletons/mapMessageResponseInstance'
import {
  IGetMessagesAuthorsRequestInterfaces,
} from '@toolify/server/src/controllers/MessageController/validators/GetMessagesAuthors/types/IGetMessagesAuthorsRequestInterfaces'
import {
  IGetMessagesRequestInterfaces,
} from '@toolify/server/src/controllers/MessageController/validators/GetMessages/types/IGetMessagesRequestInterfaces'
import {
  IUploadFileToMessageRequestInterfaces,
} from '@toolify/server/src/controllers/MessageController/validators/UploadFileToMessage/types/IUploadFileToMessageRequestInterfaces'
import {AxiosProgressEvent} from 'axios'
import {
  IGetMessageRequestInterfaces,
} from '@toolify/server/src/controllers/MessageController/validators/GetMessage/types/IGetMessageRequestInterfaces'
import {
  IDeleteMessageRequestInterfaces,
} from '@toolify/server/src/controllers/MessageController/validators/DeleteMessage/types/IDeleteMessageRequestInterfaces'
import {
  IUpdateMessageRequestInterfaces,
} from '@toolify/server/src/controllers/MessageController/validators/UpdateMessage/types/IUpdateMessageRequestInterfaces'
import {MessageState} from '@toolify/server/src/models/mongoose/MessageModel/enum/MessageState'
import {
  IDeleteFileFromMessageRequestInterfaces,
} from '@toolify/server/src/controllers/MessageController/validators/DeleteFileFromMessage/types/IDeleteFileFromMessageRequestInterfaces'
import {IMarkMessagesAsReadRequestInterfaces} from '@toolify/server/src/controllers/MessageController/validators/MarkMessagesAsRead/types/IMarkMessagesAsReadRequestInterfaces'
import {IMarkMessagesAsUnreadRequestInterfaces} from '@toolify/server/src/controllers/MessageController/validators/MarkMessagesAsUnread/types/IMarkMessagesAsUnreadRequestInterfaces'
import {IGetSessionToolPermissionsRequestInterfaces} from '@toolify/server/src/controllers/MessageController/validators/GetSessionToolPermissions/types/IGetSessionToolPermissionsRequestInterfaces'
import {IRevokeSessionToolPermissionsRequestInterfaces} from '@toolify/server/src/controllers/MessageController/validators/RevokeSessionToolPermissions/types/IRevokeSessionToolPermissionsRequestInterfaces'
import {mapResponseEntity} from '@toolify/client/src/modules/api/singletons/mapResponseEntity'
import {
  IGetConversationContextRequestInterfaces,
} from '@toolify/server/src/controllers/MessageController/validators/GetConversationContext/types/IGetConversationContextRequestInterfaces'
import {IResponseEntity} from '@toolify/server/src/services/EntityService/types/IResponseEntity'
import {IGetMessageReadEntitiesRequestInterfaces} from './../../../../../server/src/controllers/MessageController/validators/GetMessageReadEntities/types/IGetMessageReadEntitiesRequestInterfaces'
import {
  IApproveLlmToolUsedRequestInterfaces,
} from '@toolify/server/src/controllers/MessageController/validators/ApproveLlmToolUsed/types/IApproveLlmToolUsedRequestInterfaces'
import {
  IRejectLlmToolUsedRequestInterfaces,
} from '@toolify/server/src/controllers/MessageController/validators/RejectLlmToolUsed/types/IRejectLlmToolUsedRequestInterfaces'
import {
  IBulkResolveLlmToolsUsedRequestInterfaces,
} from '@toolify/server/src/controllers/MessageController/validators/BulkResolveLlmToolsUsed/types/IBulkResolveLlmToolsUsedRequestInterfaces'
import {
  IStopLlmResponseRequestInterfaces,
} from '@toolify/server/src/controllers/MessageController/validators/StopLlmResponse/types/IStopLlmResponseRequestInterfaces'
import {
  ICompactLlmContextRequestInterfaces,
} from '@toolify/server/src/controllers/MessageController/validators/CompactLlmContext/types/ICompactLlmContextRequestInterfaces'
import {
  IFinishLlmWaitRequestInterfaces,
} from '@toolify/server/src/controllers/MessageController/validators/FinishLlmWait/types/IFinishLlmWaitRequestInterfaces'
import {LlmWaitFinishActionType} from '@toolify/server/src/services/LlmService/enum/LlmWaitFinishActionType'
import {
  IGetLlmContextSnapshotRequestInterfaces,
} from '@toolify/server/src/controllers/MessageController/validators/GetLlmContextSnapshot/types/IGetLlmContextSnapshotRequestInterfaces'
import {
  IPreviewLlmResourceContextRequestInterfaces,
} from '@toolify/server/src/controllers/MessageController/validators/PreviewLlmResourceContext/types/IPreviewLlmResourceContextRequestInterfaces'

export const useMessageApiStore = defineStore('messageApi', {
  actions: {
    async createMessage<T extends ConversationType>(
      conversationType: T,
      conversationTypePayload: IConversationTypeInterfacesMap[ConversationType]['conversationTypePayload'],
      content: JSONContent[],
      replyMessageId:string | null,
      state = MessageState.Sent,
      callId: string | null = null,
      llmModelKey: string | null = null,
      llmRequest: ILlmRequestPayload | null = null,
    ): Promise<ICreateMessageRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {message} = await apiRequestStore.post<ICreateMessageRequestInterfaces>(
        `/conversation-type/${conversationType}/message`,
        {
          data: {
            content,
            conversationTypePayload,
            state,
            replyMessageId,
            callId,
            ...(llmModelKey ? {llmModelKey} : {}),
            ...(llmRequest ? {llmRequest} : {}),
          },
        },
      )
      return {
        message: mapMessageResponseInstance(message),
      }
    },

    async updateMessage(
      messageId: string,
      data: IUpdateMessageRequestInterfaces['body'],
    ): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.patch<IUpdateMessageRequestInterfaces>(
        `/message/${messageId}`,
        {
          data,
        },
      )
    },

    async approveLlmToolUsed(
      messageId: string,
    ): Promise<IApproveLlmToolUsedRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {message} = await apiRequestStore.post<IApproveLlmToolUsedRequestInterfaces>(
        `/message/${messageId}/approve-llm-tool-used`,
      )

      return {
        message: mapMessageResponseInstance(message),
      }
    },

    async rejectLlmToolUsed(
      messageId: string,
    ): Promise<IRejectLlmToolUsedRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {message} = await apiRequestStore.post<IRejectLlmToolUsedRequestInterfaces>(
        `/message/${messageId}/reject-llm-tool-used`,
      )

      return {
        message: mapMessageResponseInstance(message),
      }
    },

    async bulkResolveLlmToolsUsed(
      triggerMessageId: string,
      action: 'approve' | 'reject',
      scope?: 'session' | 'always' | 'always-scoped',
    ): Promise<IBulkResolveLlmToolsUsedRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {resolvedCount} = await apiRequestStore.post<IBulkResolveLlmToolsUsedRequestInterfaces>(
        `/message/${triggerMessageId}/bulk-resolve-llm-tools-used`,
        {
          data: {
            action,
            scope,
          },
        },
      )

      return {resolvedCount}
    },

    async resolveLlmToolUsed(
      {messageId, triggerMessageId}: {messageId: string, triggerMessageId?: string | null},
      action: 'approve' | 'reject',
      scope?: 'session' | 'always' | 'always-scoped',
    ): Promise<void> {
      if (triggerMessageId) {
        await this.bulkResolveLlmToolsUsed(triggerMessageId, action, scope)
      } else if (action === 'approve') {
        await this.approveLlmToolUsed(messageId)
      } else {
        await this.rejectLlmToolUsed(messageId)
      }
    },

    async stopLlmResponse(
      messageId: string,
    ): Promise<IStopLlmResponseRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.post<IStopLlmResponseRequestInterfaces>(
        `/message/${messageId}/stop-llm-response`,
      )
    },

    async setLlmMessageFeedback(
      messageId: string,
      value: LlmMessageFeedbackType | null,
    ): Promise<ISetLlmMessageFeedbackRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.put<ISetLlmMessageFeedbackRequestInterfaces>(
        `/message/${messageId}/llm-feedback`,
        {
          data: {
            value,
          },
        },
      )
    },

    async finishLlmWait(
      messageId: string,
      action: LlmWaitFinishActionType,
    ): Promise<IFinishLlmWaitRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.post<IFinishLlmWaitRequestInterfaces>(
        `/message/${messageId}/finish-llm-wait`,
        {
          data: {
            action,
          },
        },
      )
    },

    async compactLlmContext(
      conversationType: ConversationType,
      conversationTypePayload: IConversationTypeInterfacesMap[ConversationType]['conversationTypePayload'],
    ): Promise<ICompactLlmContextRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {message} = await apiRequestStore.post<ICompactLlmContextRequestInterfaces>(
        `/conversation-type/${conversationType}/compact-llm-context`,
        {
          data: {
            conversationTypePayload,
          },
        },
      )
      return {
        message: message ? mapMessageResponseInstance(message) : null,
      }
    },

    async getLlmContextSnapshot(
      conversationType: ConversationType,
      conversationTypePayload: IConversationTypeInterfacesMap[ConversationType]['conversationTypePayload'],
    ): Promise<IGetLlmContextSnapshotRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.post<IGetLlmContextSnapshotRequestInterfaces>(
        `/conversation-type/${conversationType}/llm-context-snapshot`,
        {
          data: {
            conversationTypePayload,
          },
        },
      )
    },

    async previewLlmResourceContext(
      data: IPreviewLlmResourceContextRequestInterfaces['body'],
    ): Promise<IPreviewLlmResourceContextRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.post<IPreviewLlmResourceContextRequestInterfaces>(
        '/llm-resource-context/preview',
        {data},
      )
    },

    async getMessagesAuthors<T extends ConversationType>(
      conversationType: T,
      conversationTypePayload: IConversationTypeInterfacesMap[ConversationType]['conversationTypePayload'],
    ): Promise<IGetMessagesAuthorsRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {messagesAuthors} = await apiRequestStore.post<IGetMessagesAuthorsRequestInterfaces>(
        `/conversation-type/${conversationType}/messages-authors`,
        {
          data: {
            conversationTypePayload,
          },
        }, {retryOnNetworkError: true}, 
      )

      return {
        messagesAuthors: messagesAuthors.map(author => {
          return mapResponseEntity(author)
        }),
      }
    },

    async getMessages<T extends ConversationType>(
      conversationType: T,
      conversationTypePayload: IConversationTypeInterfacesMap[ConversationType]['conversationTypePayload'],
      skip = 0,
      messageId = null,
      callId: string | null = null,
    ): Promise<IGetMessagesRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {messages} = await apiRequestStore.post<IGetMessagesRequestInterfaces>(
        `/conversation-type/${conversationType}/messages`,
        {
          data: {
            conversationTypePayload,
            skip,
            messageId,
            callId,
          },
        }, {retryOnNetworkError: true},
      )
      return {
        messages: messages.map(message => {
          return mapMessageResponseInstance(message)
        }),
      }
    },

    async markMessagesAsRead<T extends ConversationType>(
      conversationType: T,
      conversationTypePayload: IConversationTypeInterfacesMap[T]['conversationTypePayload'],
      callId: string | null = null,
    ): Promise<{lastUnreadMessageId:string | null, unreadCount:number, unseenCount: number, newLastReadMessageId: string | null }> {
      const apiRequestStore = useApiRequestStore()
      const response = await apiRequestStore.post<IMarkMessagesAsReadRequestInterfaces>(
        `/conversation-type/${conversationType}/mark-messages-as-read`,
        {
          data: {
            conversationTypePayload,
            callId,
          },
        })
      return {
        lastUnreadMessageId: response.lastUnreadMessageId,
        unreadCount: response.unreadCount,
        unseenCount: response.unseenCount,
        newLastReadMessageId: response.newLastReadMessageId,
      }
    },

    async markMessagesAsUnread<T extends ConversationType>(
      conversationType: T,
      conversationTypePayload: IConversationTypeInterfacesMap[T]['conversationTypePayload'],
      callId: string | null = null,
    ): Promise<IMarkMessagesAsUnreadRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const response = await apiRequestStore.post<IMarkMessagesAsUnreadRequestInterfaces>(
        `/conversation-type/${conversationType}/mark-messages-as-unread`,
        {
          data: {
            conversationTypePayload,
            callId,
          },
        })
      return {
        lastReadMessageId: response.lastReadMessageId,
        unreadCount: response.unreadCount,
      }
    },

    async getSessionToolPermissions<T extends ConversationType>(
      conversationType: T,
      conversationTypePayload: IConversationTypeInterfacesMap[T]['conversationTypePayload'],
    ): Promise<IGetSessionToolPermissionsRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {toolNames} = await apiRequestStore.post<IGetSessionToolPermissionsRequestInterfaces>(
        `/conversation-type/${conversationType}/session-tool-permissions`,
        {
          data: {
            conversationTypePayload,
          },
        }, {retryOnNetworkError: true})
      return {toolNames}
    },

    async revokeSessionToolPermissions<T extends ConversationType>(
      conversationType: T,
      conversationTypePayload: IConversationTypeInterfacesMap[T]['conversationTypePayload'],
      toolName?: string,
    ): Promise<IRevokeSessionToolPermissionsRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {removedCount} = await apiRequestStore.post<IRevokeSessionToolPermissionsRequestInterfaces>(
        `/conversation-type/${conversationType}/session-tool-permissions/revoke`,
        {
          data: {
            conversationTypePayload,
            toolName,
          },
        })
      return {removedCount}
    },

    async getMessage(
      messageId: string,
    ): Promise<IGetMessageRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {message} = await apiRequestStore.get<IGetMessageRequestInterfaces>(
        `/message/${messageId}`,
      )
      return {
        message: mapMessageResponseInstance(message),
      }
    },

    async deleteMessage(
      messageId: string,
    ): Promise<IDeleteMessageRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {message} = await apiRequestStore.delete<IDeleteMessageRequestInterfaces>(
        `/message/${messageId}`,
      )
      return {
        message: mapMessageResponseInstance(message),
      }
    },
    async deleteFileFromSentMessage(
      messageId: string,
      fileId: string,
    ): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.delete<IDeleteMessageRequestInterfaces>(
        `/message/${messageId}/file/${fileId}`,
      )
    },

    async uploadFileToMessage(
      messageId: string,
      fileToUpload: File,
      onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
      signal?: AbortSignal,
    ): Promise<IUploadFileToMessageRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const formData = new FormData()
      formData.append('file', fileToUpload)
      const {file} = await apiRequestStore.post<IUploadFileToMessageRequestInterfaces>(
        `/message/${messageId}/file`,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          data: formData,
          onUploadProgress,
          signal,
        },
      )
      return {
        file,
      }
    },

    async deleteFileFromMessage(
      fileId: string,
    ): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.delete<IDeleteFileFromMessageRequestInterfaces>(
        `/message-file/${fileId}`,
      )
    },

    async getConversationContext<T extends ConversationType>(
      conversationType: T,
      conversationTypePayload: IConversationTypeInterfacesMap[ConversationType]['conversationTypePayload'],
    ): Promise<{
      conversationContext: IConversationTypeInterfacesMap[T]['conversationContext']
      canDeleteOthersMessages: boolean
    }> {
      const apiRequestStore = useApiRequestStore()
      const {conversationContext, canDeleteOthersMessages} = await apiRequestStore.post<IGetConversationContextRequestInterfaces>(
        `/conversation-type/${conversationType}/context`,
        {
          data: {
            conversationTypePayload,
          },
        }, {retryOnNetworkError: true},
      )
      return {
        conversationContext: conversationContext as IConversationTypeInterfacesMap[T]['conversationContext'],
        canDeleteOthersMessages: !!canDeleteOthersMessages,
      }
    },
    async getMessageReadEntities(
      messageId: string,
    ): Promise<IResponseEntity[]> {
      const apiRequestStore = useApiRequestStore()
      const {entities} = await apiRequestStore.get<IGetMessageReadEntitiesRequestInterfaces>(
        `/message/${messageId}/read-entities`,
      )
      return entities.map(entity => mapResponseEntity(entity))
    },
  },
})
